/**
 * MADE-289 (rev 2): CustomerOrderPackSlip — printable packing slip.
 *
 * Root cause fix (UAT follow-up):
 *   The MUI Drawer is itself a portal that appends a container div to document.body.
 *   The rule `body > * { display: none !important }` hides that container div during
 *   print, and any child inside it — even with `display: block !important` — stays
 *   hidden because its parent is hidden.
 *
 *   Fix: use ReactDOM.createPortal to render the slip document as a DIRECT child of
 *   document.body. `body > .print-pack-slip-portal.active { display: flex !important }`
 *   can then un-hide it independently of the drawer's portal container.
 *
 * Screen behaviour: renders only a "Print Slip" button (nothing else).
 * Print behaviour: the portal div becomes visible (via .active + @media print rules).
 *
 * Multiple-instance safety: when several Kanban cards are in pack_and_ship state
 * (each mounting one portal), only the clicked card's portal gets the `.active`
 * class, so only that slip prints.
 *
 * Print format: 8.5 × 11 in portrait; slip occupies top 5.5 in by default and
 * expands to full page if the items list is longer.
 *
 * Layout (top → bottom):
 *   1. Header row: Made.bb logo (left) | Order number + print date (right)
 *   2. Two-column address block: FROM | SHIP TO / COLLECT
 *   3. Divider
 *   4. Items table: Qty | Description
 *   5. Divider
 *   6. Signature section (margin-top: auto → anchors to bottom of content)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Chip,
  Button,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import type { PackSlipData } from '../../types/madebb.types';

// ── Constants ─────────────────────────────────────────────────────────────────

const MADEBB_ADDRESS = {
  name: 'Made.bb',
  line1: 'Six Crossroads Industrial Estate',
  line2: 'Six Crossroads, St Philip',
  line3: 'Barbados BB18056',
};

/** Logo asset served at /madebb/logo.png */
const LOGO_SRC = '/madebb/logo.png';

const SHIPPING_CHIP: Record<string, { label: string; color: 'success' | 'info' | 'default' }> = {
  collect:       { label: 'Collect',       color: 'success' },
  local_dhl:     { label: 'Local DHL',     color: 'info'    },
  international: { label: 'International', color: 'default' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPrintDate(): string {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CustomerOrderPackSlipProps {
  packSlipData: PackSlipData;
  orderNumber?: string;
  onClose?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const CustomerOrderPackSlip: React.FC<CustomerOrderPackSlipProps> = ({
  packSlipData,
  orderNumber,
  onClose,
}) => {
  const shipping     = packSlipData.shipping;
  const customer     = packSlipData.customer;
  const items        = packSlipData.order_items ?? [];
  const displayOrder = orderNumber ?? packSlipData.order_number;
  const isCollect    = shipping?.method === 'collect';
  const chipConfig   = shipping
    ? (SHIPPING_CHIP[shipping.method] ?? { label: shipping.method, color: 'default' as const })
    : null;

  // ── Print-active state ───────────────────────────────────────────────────
  // When multiple instances are on screen (Kanban cards), only the clicked
  // one should show during print. The `.active` class gates the @media print rule.
  const [isPrinting, setIsPrinting] = useState(false);

  // ── Print CSS injection ──────────────────────────────────────────────────
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-pack-slip-print', 'true');
    style.textContent = [
      /* Always hidden on screen */
      '.print-pack-slip-portal { display: none !important; }',
      '@media print {',
      '  /* Hide everything except our active portal */',
      '  body > * { display: none !important; }',
      '  body > .print-pack-slip-portal.active {',
      '    display: flex !important;',
      '    flex-direction: column;',
      '    width: 8.5in;',
      '    min-height: 5.5in;',
      '    padding: 0.5in;',
      '    box-sizing: border-box;',
      '    font-size: 11pt;',
      '    font-family: "Inter", "Roboto", "Helvetica Neue", sans-serif;',
      '    color: #000;',
      '  }',
      '  /* Keep the selected slip visible if another print rule hides page content. */',
      '  body > .print-pack-slip-portal.active,',
      '  body > .print-pack-slip-portal.active * { visibility: visible !important; }',
      '  @page { size: 8.5in 11in portrait; margin: 0; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // ── Print handler ────────────────────────────────────────────────────────
  // Sets this instance's portal to `.active` so only its slip prints, then
  // calls window.print(). Double-rAF ensures React flushes the state update
  // (and re-renders the `.active` class) before the browser opens the dialog.
  const handlePrint = useCallback(() => {
    setIsPrinting(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setIsPrinting(false);
      });
    });
  }, []);

  // ── Address helper ───────────────────────────────────────────────────────
  const addr = shipping?.address ?? null;

  // ── Full slip document (rendered into portal → direct child of body) ─────
  const slipDocument = (
    <div
      className={`print-pack-slip-portal${isPrinting ? ' active' : ''}`}
      data-testid="customer-order-pack-slip"
    >
      {/* ── 1. Header row ───────────────────────────────────────────────── */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        {/* Logo */}
        <Box>
          <img
            src={LOGO_SRC}
            alt="Made.bb"
            data-testid="madebb-logo"
            style={{ height: '40px', objectFit: 'contain' }}
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.display = 'none';
              const fallback = document.createElement('span');
              fallback.textContent = 'Made.bb';
              fallback.style.cssText =
                'font-size:1.4rem;font-weight:800;color:#1a1a2e;letter-spacing:-0.5px;';
              target.parentElement?.appendChild(fallback);
            }}
          />
        </Box>

        {/* Order info */}
        <Box textAlign="right">
          <Typography
            variant="h6"
            fontWeight={700}
            lineHeight={1.2}
            sx={{ fontSize: '1rem' }}
            data-testid="pack-slip-order-number"
          >
            ORDER #{displayOrder}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.8rem', mt: 0.25 }}
            data-testid="pack-slip-print-date"
          >
            Date: {formatPrintDate()}
          </Typography>
        </Box>
      </Stack>

      {/* ── 2. Two-column address block ─────────────────────────────────── */}
      <Stack direction="row" spacing={0} mb={2} sx={{ gap: 0 }}>
        {/* FROM */}
        <Box
          flex={1}
          pr={2}
          sx={{ borderRight: '1px solid', borderColor: 'divider' }}
          data-testid="pack-slip-from"
        >
          <Typography
            variant="overline"
            fontWeight={700}
            sx={{ fontSize: '0.65rem', letterSpacing: '0.08em', color: '#666' }}
          >
            FROM
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>
            {MADEBB_ADDRESS.name}
          </Typography>
          <Typography variant="body2">{MADEBB_ADDRESS.line1}</Typography>
          <Typography variant="body2">{MADEBB_ADDRESS.line2}</Typography>
          <Typography variant="body2">{MADEBB_ADDRESS.line3}</Typography>
        </Box>

        {/* SHIP TO / COLLECT */}
        <Box
          flex={1}
          pl={2}
          data-testid="pack-slip-ship-to"
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
            <Typography
              variant="overline"
              fontWeight={700}
              sx={{ fontSize: '0.65rem', letterSpacing: '0.08em', color: '#666' }}
            >
              {isCollect ? 'FOR COLLECTION' : 'SHIP TO'}
            </Typography>
            {chipConfig && (
              <Chip
                label={chipConfig.label}
                color={chipConfig.color}
                size="small"
                sx={{ height: 16, fontSize: '0.6rem', px: 0.25 }}
                data-testid="shipping-method-badge"
              />
            )}
          </Stack>

          {/* Customer name — always shown */}
          {customer?.name && (
            <Typography variant="body2" fontWeight={700}>
              {customer.name}
            </Typography>
          )}

          {/* Collect: show email + phone directly under name */}
          {isCollect && customer?.email && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
              {customer.email}
            </Typography>
          )}
          {isCollect && customer?.phone && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
              {customer.phone}
            </Typography>
          )}

          {/* Full address block — shown for non-collect orders when available */}
          {addr && !isCollect && (
            <Box mt={0.25} data-testid="delivery-address">
              {addr.company && (
                <Typography variant="body2">{addr.company}</Typography>
              )}
              {addr.address_line_1 && (
                <Typography variant="body2">{addr.address_line_1}</Typography>
              )}
              {addr.address_line_2 && (
                <Typography variant="body2">{addr.address_line_2}</Typography>
              )}
              {(addr.city || addr.province) && (
                <Typography variant="body2">
                  {[addr.city, addr.province].filter(Boolean).join(', ')}
                </Typography>
              )}
              {addr.postal_code && (
                <Typography variant="body2">{addr.postal_code}</Typography>
              )}
              {addr.country && (
                <Typography variant="body2">{addr.country}</Typography>
              )}
            </Box>
          )}

          {/* DHL/international: show email + phone below address */}
          {!isCollect && customer?.email && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.25 }}>
              {customer.email}
            </Typography>
          )}
          {!isCollect && customer?.phone && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
              {customer.phone}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* ── 3. Divider ──────────────────────────────────────────────────── */}
      <Divider sx={{ mb: 1.5 }} />

      {/* ── 4. Items table ──────────────────────────────────────────────── */}
      <Box mb={1.5} data-testid="pack-slip-items-section">
        {/* Table header */}
        <Stack
          direction="row"
          sx={{
            pb: 0.5,
            mb: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="overline"
            fontWeight={700}
            sx={{ width: 48, fontSize: '0.65rem', letterSpacing: '0.08em', color: '#666', flexShrink: 0 }}
          >
            QTY
          </Typography>
          <Typography
            variant="overline"
            fontWeight={700}
            sx={{ fontSize: '0.65rem', letterSpacing: '0.08em', color: '#666' }}
          >
            DESCRIPTION
          </Typography>
        </Stack>

        {/* Item rows */}
        {items.length > 0 ? (
          <Box data-testid="pack-slip-items">
            {items.map((item, idx) => (
              <Stack
                key={idx}
                direction="row"
                alignItems="baseline"
                sx={{
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'rgba(0,0,0,0.06)',
                  '&:last-child': { borderBottom: 'none' },
                }}
                data-testid={`pack-slip-item-${idx}`}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ width: 48, flexShrink: 0, fontSize: '0.88rem' }}
                >
                  {item.qty}
                </Typography>
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '0.88rem' }}>
                    {item.name}
                    {item.variant_label ? (
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        {' '}— {item.variant_label}
                      </Box>
                    ) : null}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No items found.
          </Typography>
        )}
      </Box>

      {/* ── 5. Divider ──────────────────────────────────────────────────── */}
      <Divider />

      {/* ── 6. Signature section ────────────────────────────────────────── */}
      {/*
       * margin-top: auto anchors this to the bottom of the slip content,
       * whether that's the 5.5" default height or the expanded full-page.
       * page-break-inside: avoid keeps it together with the rows above.
       */}
      <Box
        mt="auto"
        pt={2}
        sx={{ pageBreakInside: 'avoid' }}
        data-testid="pack-slip-signature"
      >
        <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.88rem' }}>
          Your order was inspected and packed by:
        </Typography>
        <Box
          sx={{
            width: '1.5in',
            borderBottom: '1.5px solid',
            borderColor: '#333',
            mb: 0.5,
          }}
        />
        <Typography
          variant="caption"
          sx={{ fontSize: '0.72rem', color: '#666', letterSpacing: '0.05em' }}
        >
          Signature
        </Typography>
      </Box>
    </div>
  );

  return (
    <>
      {/* Screen only: the only thing visible in the component's render */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={handlePrint} data-testid="print-slip-btn">
          Print Slip
        </Button>
        {onClose && (
          <Button variant="text" size="small" onClick={onClose} data-testid="close-slip-btn">
            Close
          </Button>
        )}
      </Stack>

      {/* Print only: portal renders slip as direct child of body.
          This bypasses the MUI Drawer portal container so the @media print
          rule `body > .print-pack-slip-portal.active` can target it directly. */}
      {createPortal(slipDocument, document.body)}
    </>
  );
};
